import { useState, useEffect, useCallback, useRef } from 'react';
// custom
import { useLanguage } from '@shared/contexts/LanguageContext';
import { PageLayout } from '@shared/layouts';
import PostcardCreateModal from '@features/postcard/components/PostcardCreateModal';
import { ROUTES } from '@app/router/routes';
import { isFeatureAccessible, FEATURE_FLAGS } from '@shared/config/featureFlags';
import { VideoSection } from '@shared/components';
import HeroBanner from '@shared/components/HeroBanner';
import { getLatestNews } from '@features/home/api/homeService';
import { getLightingPostList } from '@features/lamp-lighting/api/lampService';
import MemberNotificationBanner from '@shared/components/MemberNotificationBanner';
import showVersion from '@/app/version';
import CloseButton from '@/shared/components/CloseButton';
import InteractiveZoneItem from '@/shared/components/InteractiveZoneItem';


const VideoCard = ({ video, getYouTubeVideoId, onClick, expanded }) => {
  const size = expanded ? '100%' : 155;
  return (
    <div
      className="cursor-pointer"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, ...(expanded ? {} : { width: 155, flexShrink: 0 }) }}
      onClick={onClick}
    >
      <div
        style={{ width: size, aspectRatio: '1', position: 'relative', background: 'white', boxShadow: '2px 4px 8px rgba(110,98,91,0.08)', borderRadius: 20, border: '1.1px solid rgba(110,98,91,0.08)', overflow: 'hidden' }}
      >
        {video.thumbnail ? (
          <img src={video.thumbnail} alt={video.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : getYouTubeVideoId(video.videoUrl) ? (
          <img src={`https://img.youtube.com/vi/${getYouTubeVideoId(video.videoUrl)}/hqdefault.jpg`} alt={video.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : video.videoUrl ? (
          <video src={`${video.videoUrl}#t=0.1`} preload="metadata" muted playsInline controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span className="material-symbols-outlined" style={{ color: 'rgba(110,98,91,0.16)', fontSize: 48 }}>play_arrow</span>
          </div>
        )}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)' }}>
          <span className="material-symbols-outlined" style={{ color: 'rgba(255,255,255,0.85)', fontSize: expanded ? 40 : 32 }}>play_arrow</span>
        </div>
      </div>
      <p className="line-clamp-2 text-center" style={{ color: 'rgba(23,23,23,0.75)', fontSize: 16, fontFamily: 'Noto Serif TC', fontWeight: 500, lineHeight: '22px', width: '100%', paddingLeft: expanded ? 4 : 16, paddingRight: expanded ? 4 : 16 }}>
        {video.uploadDate && `${new Date(video.uploadDate).getMonth() + 1}/${new Date(video.uploadDate).getDate()} `}{video.title}
      </p>
    </div>
  );
};

const LandingPage = ({ user, memberNotification = '', onDismissNotification }) => {
  const { t, currentLanguage } = useLanguage();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isPostcardModalOpen, setIsPostcardModalOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem('splashShown'));
  const [isExiting, setIsExiting] = useState(false);
  const isLoggedIn = !!user;
  const isDnaEnabled = isFeatureAccessible(FEATURE_FLAGS.DNA, isLoggedIn);
  const isQuizEnabled = isFeatureAccessible(FEATURE_FLAGS.QUIZ, isLoggedIn);
  const isPostcardEnabled = isFeatureAccessible(FEATURE_FLAGS.POSTCARD, isLoggedIn);
  const isLiveCommentEnabled = isFeatureAccessible(FEATURE_FLAGS.LIVE_COMMENT, isLoggedIn);
  const isJueGuangEnabled = isFeatureAccessible(FEATURE_FLAGS.JUE_GUANG, isLoggedIn);
  const isPersonaEnabled = isFeatureAccessible(FEATURE_FLAGS.PERSONA, isLoggedIn);
  const isWishTreeEnabled = isFeatureAccessible(FEATURE_FLAGS.WISH_TREE, isLoggedIn);
  const [splashImage] = useState(() => {
    const images = [
      '/image/splash/splash_1.jpg',
      '/image/splash/splash_2.jpg',
      '/image/splash/splash_3.jpg',
      '/image/splash/splash_4.jpg',
    ];
    return images[Math.floor(Math.random() * images.length)];
  });
  const [activeSlide, setActiveSlide] = useState(0);
  const [bannerDetails, setBannerDetails] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [videos, setVideos] = useState([]);
  // 最新消息 popup
  const [selectedNews, setSelectedNews] = useState(null);
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);
  // 最新消息展開
  const NEWS_INITIAL = 3;
  const NEWS_STEP = 3;
  const [newsVisible, setNewsVisible] = useState(NEWS_INITIAL);
  // 記錄每則消息描述是否溢出（需要顯示「顯示更多」按鈕）
  const [newsOverflow, setNewsOverflow] = useState({});
  const newsDescRefs = useRef({});
  // 最新影音展開
  const VIDEOS_INITIAL = 4;
  const VIDEOS_STEP = 4;
  const [videosVisible, setVideosVisible] = useState(VIDEOS_INITIAL);

  // Splash Screen Timer
  useEffect(() => {
    if (!showSplash) return;
    const timer = setTimeout(() => {
      setIsExiting(true);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  // 取得最新消息 & 最新影音
  useEffect(() => {
    const fetchLatestData = async () => {
      try {
        const [newsRes, videosRes] = await Promise.all([
          getLatestNews(1),
          getLatestNews(2),
        ]);

        // 最新消息：pinned 排前，再接 items，全部取回（展開時用）
        const allNews = [...(newsRes.pinned_items || []), ...(newsRes.items || [])];
        setNewsItems(allNews.map((item) => {
          const date = new Date(item.event_time || item.published_at);
          return {
            id: item.news_id ?? `${item.event_id}-${item.post_id ?? item.news_id}`,
            newsId: item.news_id,
            postId: item.post_id,
            eventId: item.event_id,
            date: {
              month: String(date.getMonth() + 1),
              day: String(date.getDate()),
            },
            title: item.title,
            description: item.content,
          };
        }));

        // 最新影音：pinned 排前，再接 items，過濾掉沒有 media_url 的項目
        const allVideos = [...(videosRes.pinned_items || []), ...(videosRes.items || [])].filter((item) => item.media_url);
        setVideos(allVideos.map((item) => ({
          id: item.news_id,
          videoUrl: item.media_url,
          thumbnail: '',
          title: item.title,
          description: item.content,
          uploadDate: item.event_time || item.published_at,
        })));
      } catch (err) {
        console.error('Failed to fetch latest news/videos:', err);
      }
    };

    fetchLatestData();
  }, [currentLanguage]);

  // 處理影片播放 - 開啟內部播放器
  const handleVideoClick = (video) => {
    console.log('Opening video:', video);
    const videoId = getYouTubeVideoId(video.videoUrl);
    console.log('Embed URL will be:', `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`);
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };

  // 關閉影片播放器
  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    setTimeout(() => setSelectedVideo(null), 300); // 延遲清除以支援關閉動畫
  };

  // 量測描述文字是否被 line-clamp 截斷
  const handleDescRef = useCallback((el, newsId) => {
    if (!el) return;
    newsDescRefs.current[newsId] = el;
    const measure = () => {
      // Use a 1px tolerance to guard against sub-pixel rounding:
      // clientHeight/scrollHeight are integers; with lineHeight:23.4px,
      // they can differ by 1px even when the actual heights are equal.
      const isOverflowing = el.scrollHeight - el.clientHeight > 1;
      setNewsOverflow((prev) => {
        if (prev[newsId] === isOverflowing) return prev;
        return { ...prev, [newsId]: isOverflowing };
      });
    };
    // Wait for web fonts (Noto Serif TC) to load before measuring,
    // otherwise the fallback font may give inaccurate dimensions.
    document.fonts.ready.then(() => requestAnimationFrame(measure));
  }, []);

  // event_id 當作 type：1=文星點燈報導、3=最新消息
  const handleNewsClick = (news) => {
    setSelectedNews(news);
    setIsNewsModalOpen(true);
  };

  const handleNewsNavigate = async (news) => {
    if (news.eventId === 1) {
      if (!news.postId) return;
      try {
        const res = await getLightingPostList({ post_id: news.postId, page: 1, per_page: 1 });
        const monasteryId = res?.items?.[0]?.monastery_id;
        if (monasteryId) {
          window.location.href = ROUTES.BRANCH_REPORT_DETAIL(monasteryId);
        }
      } catch (err) {
        console.error('Failed to fetch lighting post for navigation:', err);
      }
      return;
    }

    if (news.eventId === 3 && news.postId) {
      window.location.href = ROUTES.NEWS_DETAIL(news.postId);
    }
  };

  const getNewsNavigateLabel = (news) => {
    if (news.eventId === 1) return t('home.newsGoToBranchReport');
    if (news.eventId === 3) return t('home.newsGoTo');
    return t('home.newsGoTo');
  };

  const handleCloseNewsModal = () => {
    setIsNewsModalOpen(false);
    setTimeout(() => setSelectedNews(null), 300);
  };

  // 從 YouTube URL 取得影片 ID
  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[2].length === 11) ? match[2] : null;
    // console.log('YouTube URL:', url, '-> Video ID:', videoId);
    return videoId;
  };

  const version = showVersion(false, true);
  const isEn = currentLanguage.code === 'en';
  const pillFontSize = isEn ? 22 : 32;
  const pillSpacing = isEn ? 3 : 7;
  const interactiveItems = [
    {
      id: 1,
      icon: "psychology",
      title: t('nav.dna'),
      subtitle: "",
      iconColor: "var(--color-primary-light)"
    },
    {
      id: 2,
      icon: "quiz",
      title: t('nav.quiz'),
      subtitle: "",
      iconColor: "var(--color-buddha-gold)"
    },
    {
      id: 3,
      icon: "favorite",
      title: t('nav.lampBlessing'),
      subtitle: "",
      iconColor: "var(--color-secondary)"
    },
    {
      id: 4,
      icon: "mail",
      title: t('nav.postcard'),
      subtitle: "",
      iconColor: "var(--color-wood)"
    }
  ];


  //--------------------------------------------------------------------------------------------
  return (
    <PageLayout
      headerProps={{
        user: user,
        showHome: true,
        subMenu: <MemberNotificationBanner message={memberNotification} onDismiss={onDismissNotification} />,
      }}
      hideFooter={true}
      noPadding={true}
    >
      {/* Splash Screen */}
      {showSplash && (
        <div
          className={`fixed inset-0 z-[10000] flex items-center justify-center ${isExiting ? 'animate-splash-exit' : ''}`}
          style={{ backgroundColor: 'var(--color-background)' }}
          onAnimationEnd={() => {
            if (isExiting) {
              sessionStorage.setItem('splashShown', '1');
              setShowSplash(false);
            }
          }}
        >
          <img
            src={splashImage}
            alt="Splash"
            className="w-full h-full object-cover md:object-contain md:max-w-md lg:max-w-lg"
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-grow w-full mx-auto flex flex-col relative overflow-hidden" style={{ backgroundColor: '#fff' }}>
        {/* 手機版背景圖（桌面隱藏）*/}
        <img
          src="/image/landing/landing_bg.jpg"
          alt=""
          className="md:hidden absolute top-0 left-0 w-full pointer-events-none select-none"
          style={{ zIndex: 0, objectFit: 'cover', objectPosition: 'top' }}
        />
        {/* 桌面版背景漸層（手機隱藏）*/}
        <div
          className="hidden md:block absolute inset-0 pointer-events-none select-none"
          style={{ zIndex: 0, background: 'linear-gradient(180deg, white 22%, rgba(255, 238, 199, 0.65) 26%, rgba(220, 183, 125, 0.65) 33%, rgba(239, 227, 207, 0.70) 50%, rgba(220, 183, 125, 0.70) 56%, #E7D0AC 65%, #F6EEDF 70%, #FCF6ED 100%)' }}
        />
        {/* Hero Slider Section */}
        <HeroBanner withNavbarOffset navbarOffset={memberNotification ? 106 : 70} onSlideChange={setActiveSlide} onBannersLoaded={setBannerDetails} />

        {/* Intro Section - 與 Banner 同步切換 */}
        {bannerDetails.length > 0 && (
          <section className="text-center px-6 md:px-8 max-w-[500px] md:max-w-4xl mx-auto pt-6 pb-6 md:pt-10 md:pb-10 relative">
            {/* 單一佔位：取 content 最長的那筆撐開容器高度 */}
            {(() => {
              const tallest = [...bannerDetails].sort((a, b) => (b.content?.length ?? 0) - (a.content?.length ?? 0))[0];
              if (!tallest) return null;
              return (
                <div className="invisible px-4 md:px-8" aria-hidden="true">
                  <h2 className="text-3xl md:text-4xl lg:text-[40px] font-bold leading-tight mb-2 font-serif tracking-widest">{tallest.title}</h2>
                  {tallest.subtitle && <p className="text-xl md:text-2xl lg:text-3xl font-bold mb-4 md:mb-6 font-serif tracking-wide">{tallest.subtitle}</p>}
                  {tallest.content && <p className="text-lg md:text-xl lg:text-2xl font-light leading-relaxed tracking-wide font-serif whitespace-pre-line">{tallest.content}</p>}
                </div>
              );
            })()}
            {/* 實際顯示層：absolute 疊在佔位上方 */}
            {bannerDetails.map((banner, index) => {
              const isActive = activeSlide % bannerDetails.length === index;
              return (
                <div
                  key={banner.id}
                  className="absolute inset-0 flex flex-col items-center justify-center px-4 md:px-8 pt-6 pb-6 md:pt-10 md:pb-10"
                  style={{
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? 'translateY(0)' : 'translateY(16px)',
                    filter: isActive ? 'blur(0px)' : 'blur(4px)',
                    transition: 'opacity 0.9s ease, transform 0.9s cubic-bezier(0.16,1,0.3,1), filter 0.7s ease',
                    pointerEvents: isActive ? 'auto' : 'none'
                  }}
                >
                  {banner.url ? (
                    <a href={banner.url} className="flex flex-col items-center">
                      <h2 className="text-3xl md:text-4xl lg:text-[40px] font-bold leading-tight mb-2 font-serif tracking-widest" style={{ color: '#004682' }}>
                        {banner.title}
                      </h2>
                      {banner.subtitle && (
                        <p className="text-xl md:text-2xl lg:text-3xl font-bold mb-4 md:mb-6 font-serif tracking-wide" style={{ color: '#BC313D' }}>
                          {banner.subtitle}
                        </p>
                      )}
                      {banner.content && (
                        <p className="text-lg md:text-xl lg:text-2xl font-light leading-relaxed tracking-wide font-serif whitespace-pre-line" style={{ color: 'rgba(23,23,23,0.90)' }}>
                          {banner.content}
                        </p>
                      )}
                    </a>
                  ) : (
                    <>
                      <h2 className="text-3xl md:text-4xl lg:text-[40px] font-bold leading-tight mb-2 font-serif tracking-widest" style={{ color: '#004682' }}>
                        {banner.title}
                      </h2>
                      {banner.subtitle && (
                        <p className="text-xl md:text-2xl lg:text-3xl font-bold mb-4 md:mb-6 font-serif tracking-wide" style={{ color: '#BC313D' }}>
                          {banner.subtitle}
                        </p>
                      )}
                      {banner.content && (
                        <p className="text-lg md:text-xl lg:text-2xl font-light leading-relaxed tracking-wide font-serif whitespace-pre-line" style={{ color: 'rgba(23,23,23,0.90)' }}>
                          {banner.content}
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* Content Sections */}
        <div className="relative z-20 w-full max-w-7xl mx-auto">
          <div className="mx-auto w-full max-w-[500px] md:w-4/5 md:max-w-none">

            {/* News Section - Section2 */}
            <section id="news-section" className="px-6 pt-16 pb-0">
              <div style={{ borderLeft: '1px solid #6E625B', paddingLeft: 20 }}>
                {/* Section Title */}
                <div className="flex items-center justify-between mb-0" style={{ borderLeft: '6px solid #BC313D', marginLeft: -20, paddingLeft: 26 }}>
                  <h2 className="font-serif font-bold leading-tight" style={{ color: '#BC313D', fontSize: 30, lineHeight: '33px' }}>
                    {t('home.newsTitleLine1')}<br />{t('home.newsTitleLine2')}
                  </h2>
                </div>
                {/* News List */}
                {(() => {
                  const displayItems = newsItems.slice(0, newsVisible);
                  return (
                    <>
                      <div
                        style={{
                          paddingTop: 24,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0,
                        }}
                      >
                        {newsItems.length === 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
                            <span style={{ fontFamily: 'Noto Serif TC', fontSize: 16, fontWeight: 400, color: 'rgba(188,49,61,0.5)', letterSpacing: '0.2em' }}>{t('home.newsEmpty')}</span>
                          </div>
                        )}
                        {displayItems.map((news) => (
                          <div
                            key={news.id}
                            className="active:scale-[0.98] transition-all"
                            style={{ paddingLeft: 12, paddingRight: 12, paddingTop: 24, paddingBottom: 24, display: 'flex', flexDirection: 'column', gap: 8 }}
                          >
                            <div
                              className="cursor-pointer"
                              onClick={() => handleNewsNavigate(news)}
                            >
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center', paddingLeft: 4, paddingRight: 4, paddingTop: 2, paddingBottom: 2 }}>
                                <span style={{ color: '#BC3823', fontSize: 24, fontFamily: 'Noto Serif TC', fontWeight: 500, lineHeight: '30.48px', letterSpacing: 0.35 }}>{news.date.month} /</span>
                                <span style={{ color: '#BC3823', fontSize: 24, fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '30.48px', letterSpacing: 0.35 }}>{news.date.day}</span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <h4 style={{ color: '#171717', fontSize: 24, fontFamily: 'Noto Serif TC', fontWeight: 500, lineHeight: '30.48px', letterSpacing: 0.35 }}>{news.title}</h4>
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
                                  <p ref={(el) => handleDescRef(el, news.id)} className="line-clamp-2" style={{ flex: 1, minWidth: 0, color: 'rgba(23,23,23,0.75)', fontSize: 18, fontFamily: 'Noto Serif TC', fontWeight: 500, lineHeight: '23.4px' }}>{news.description}</p>
                                  {newsOverflow[news.id] && (
                                    <button className="flex-shrink-0 cursor-pointer font-serif text-sm text-accent-red underline" style={{ lineHeight: '23.4px' }} onClick={(e) => { e.stopPropagation(); handleNewsClick(news); }}>{t('home.showMore')}</button>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                              <button
                                type="button"
                                onClick={() => handleNewsClick(news)}
                                className="font-serif"
                                style={{ color: '#BC313D', fontSize: 15, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
                              >
                                More
                              </button>
                            </div> */}
                          </div>
                        ))}
                        <div style={{ height: 0, borderTop: '1px solid #6E625B' }} />
                      </div>
                      {/* 查看更多 */}
                      {newsVisible < newsItems.length && (
                        <div className="flex justify-center pt-4 pb-2">
                          <button
                            onClick={() => setNewsVisible((v) => v + NEWS_STEP)}
                            className="flex items-center gap-1 font-serif tracking-wide"
                            style={{ color: '#BC313D', fontSize: 16, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            {t('home.viewMore')}
                            <span className="material-symbols-outlined text-base">expand_more</span>
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
                {/* line blank */}
                <div className="h-10" />
              </div>
            </section>

            {/* Videos Section - Section3 */}
            <section className="px-6">
              {/* line blank */}
              <div className="h-14" style={{ borderLeft: '1px solid #6E625B', paddingLeft: 20 }} />
              {/* Section Title */}
              <div className="flex items-center justify-between" style={{ borderLeft: '6px solid #BC313D', paddingLeft: 32 }}>
                <h2 className="font-serif font-bold" style={{ color: '#BC313D', fontSize: 30, lineHeight: '33px' }}>
                  {t('home.newsTitleLine1')}<br />{t('home.videosTitleLine2')}
                </h2>
              </div>
              {/* Videos with left brown border */}
              <div style={{ borderLeft: '1px solid #6E625B', paddingLeft: 32, paddingTop: 32, paddingBottom: 16 }}>
                {videos.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
                    <span style={{ fontFamily: 'Noto Serif TC', fontSize: 16, fontWeight: 400, color: 'rgba(188,49,61,0.5)', letterSpacing: '0.2em' }}>{t('home.newsEmpty')}</span>
                  </div>
                ) : videosVisible <= VIDEOS_INITIAL ? (
                  /* 初始：橫向 scroll，顯示前4個 */
                  <div className="overflow-x-auto no-scrollbar" style={{ display: 'flex', gap: 12 }}>
                    {videos.slice(0, VIDEOS_INITIAL).map((video) => (
                      <VideoCard key={video.id} video={video} getYouTubeVideoId={getYouTubeVideoId} onClick={() => handleVideoClick(video)} />
                    ))}
                  </div>
                ) : (
                  /* 展開：2欄 grid，顯示 videosVisible 則 */
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {videos.slice(0, videosVisible).map((video) => (
                      <VideoCard key={video.id} video={video} getYouTubeVideoId={getYouTubeVideoId} onClick={() => handleVideoClick(video)} expanded />
                    ))}
                  </div>
                )}
                {/* 查看更多 */}
                {videosVisible < videos.length && (
                  <div className="flex justify-center pt-4 pb-2">
                    <button
                      onClick={() => setVideosVisible((v) => v + VIDEOS_STEP)}
                      className="flex items-center gap-1 font-serif tracking-wide"
                      style={{ color: '#BC313D', fontSize: 16, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      {t('home.viewMore')}
                      <span className="material-symbols-outlined text-base">expand_more</span>
                    </button>
                  </div>
                )}
              </div>
              {/* line blank */}
              <div className="h-10" style={{ borderLeft: '1px solid #6E625B', paddingLeft: 20 }} />
            </section>

            {/* Interactive Section - Section4 */}
            <section className="px-6 pb-10">
              {/* line blank */}
              <div className="h-10" style={{ borderLeft: '1px solid #6E625B', paddingLeft: 20 }} />
              {/* Section Title */}
              <div style={{ borderLeft: '6px solid #BC313D', paddingLeft: 32 }}>
                <h2 className="font-serif font-bold" style={{ color: '#BC313D', fontSize: 30, lineHeight: '33px' }}>
                  {t('home.interactiveTitleLine1')}<br />{t('home.interactiveTitleLine2')}
                </h2>
              </div>
              {/* Interactive Items with left brown border */}
              <div style={{ borderLeft: '1px solid #6E625B', paddingLeft: 32, paddingTop: 32, paddingBottom: 32, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0 }}><div className="md:mx-auto md:flex md:flex-col md:items-center">
                {/* Zone 1 - 護法DNA */}
                <InteractiveZoneItem
                  href={ROUTES.DNA}
                  enabled={isDnaEnabled}
                  iconSide="left"
                  pillColor="rgba(0,70,130,0.50)"
                  pillInsetColor="#B1C2D0"
                  pillPaddingLeft={95}
                  label={t('nav.dnaShort')}
                  labelFontSize={pillFontSize}
                  labelSpacing={pillSpacing}
                  mascotSrc="/image/mascot/NEW_goldbaby.png"
                  mascotStyle={{ width: 80, height: 123, left: 3, top: 0 }}
                />
                {/* Zone 2 - 點燈祈福 */}
                <InteractiveZoneItem
                  href="/lamp-bulletin"
                  iconSide="right"
                  pillColor="rgba(197,155,90,0.60)"
                  pillInsetColor="#E8D7BE"
                  pillPaddingRight={105}
                  label={t('nav.lampBlessingShort')}
                  labelFontSize={pillFontSize}
                  labelSpacing={pillSpacing}
                  mascotSrc="/image/mascot/NEW_Elephant.png"
                  mascotStyle={{ width: 152, height: 106, right: 0, bottom: 0 }}
                />
                {/* Zone 3 - 中台知多少？ */}
                <InteractiveZoneItem
                  href={ROUTES.QUIZ}
                  enabled={isQuizEnabled}
                  iconSide="left"
                  pillColor="#F2946C"
                  pillInsetColor="#F0CEBF"
                  pillPaddingLeft={85}
                  label={t('nav.quizShort')}
                  labelFontSize={pillFontSize}
                  labelSpacing={isEn ? 3 : 4}
                  mascotSrc="/image/mascot/NEW_Deer.png"
                  mascotStyle={{ width: 90, height: 120, left: 0, top: 0 }}
                />
                {/* Zone 4 - 寄送明信片 */}
                <InteractiveZoneItem
                  href={ROUTES.POSTCARD_CREATE}
                  enabled={isPostcardEnabled}
                  onClick={(e) => { e.preventDefault(); setIsPostcardModalOpen(true); }}
                  iconSide="right"
                  pillColor="rgba(188,49,61,0.60)"
                  pillInsetColor="#F2CDCD"
                  pillPaddingRight={90}
                  label={t('nav.postcardShort')}
                  labelFontSize={pillFontSize}
                  labelSpacing={pillSpacing}
                  mascotSrc="/image/mascot/NEW_Lion.png"
                  mascotStyle={{ width: 141, height: 120, right: -16, top: 5 }}
                />
                {/* Zone 5 - 禪心連線 */}
                <InteractiveZoneItem
                  href={ROUTES.LIVE_COMMENT}
                  enabled={isLiveCommentEnabled}
                  iconSide="left"
                  pillColor="#F5DE74"
                  pillInsetColor="#FAF0B0"
                  pillPaddingLeft={100}
                  label={t('nav.liveCommentShort')}
                  labelFontSize={pillFontSize}
                  labelSpacing={pillSpacing}
                  mascotSrc="/image/mascot/GoldenBoy_住.png"
                  mascotStyle={{ width: 90, height: 120, left: 3, top: 8 }}
                />
                {/* Zone 6 - 庭影覺光 */}
                <InteractiveZoneItem
                  href={ROUTES.JUE_GUANG}
                  enabled={isJueGuangEnabled}
                  iconSide="right"
                  pillColor="#A8BF71"
                  pillInsetColor="#D4E5B0"
                  pillPaddingRight={105}
                  label={t('nav.jueGuangShort')}
                  labelFontSize={pillFontSize}
                  labelSpacing={pillSpacing}
                  mascotSrc="/image/mascot/WhiteElephant_住.png"
                  mascotStyle={{ width: 120, height: 120, right: -5, bottom: 0 }}
                />
                {/* Zone 7 - 願心樹發願 */}
                <InteractiveZoneItem
                  href={ROUTES.WISH_TREE}
                  enabled={isWishTreeEnabled}
                  iconSide="left"
                  pillColor="#58B2DCB2" //與figma顏色相同
                  pillInsetColor="#B1C2D0"
                  pillPaddingLeft={100}
                  label={t('nav.wishTreeShort')}
                  labelFontSize={pillFontSize}
                  labelSpacing={pillSpacing}
                  mascotSrc="/image/mascot/WhiteDeer_住.png"
                  mascotStyle={{ width: 130, height: 130, left: -10, top: -3 }}
                />
              </div></div>
            </section>

            {/* 臨時導航連結區 - 測試用 */}
            {/* <section className="mt-8 md:mt-12 p-4 bg-amber-50/50 border border-amber-200 rounded-2xl">
            <div className="flex items-center gap-2 mb-4 px-2">
              <span className="material-symbols-outlined text-amber-600">info</span>
              <h2 className="text-lg md:text-xl font-bold text-amber-800">
                臨時頁面導航（測試用）
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {[
                { path: ROUTES.WISDOM_LAMP, label: '傳燈活動主頁', icon: 'light_mode' },
                { path: ROUTES.LAMP_SCHEDULE, label: '傳燈時程表', icon: 'schedule' },
                { path: ROUTES.BRANCH_REPORTS, label: '精舍報導列表', icon: 'article' },
                { path: ROUTES.BRANCH_REPORT_DETAIL(1), label: '精舍報導詳情', icon: 'description' },
                { path: ROUTES.LAMP_LIGHTING, label: '線上祈福點燈', icon: 'emoji_objects' },
                { path: ROUTES.LAMP_BULLETIN, label: '光照大千佈告欄', icon: 'dashboard' },
                { path: ROUTES.BLESSING_RESULT, label: '祈福結果頁', icon: 'check_circle' },
                { path: ROUTES.WHEEL, label: '轉盤頁', icon: 'casino' },
              ].map((link) => (
                <a
                  key={link.path}
                  href={link.path}
                  className="flex items-center gap-2 p-3 md:p-4 bg-white rounded-xl border border-amber-100 hover:border-amber-300 hover:bg-amber-50 transition-all group"
                >
                  <span className="material-symbols-outlined text-amber-600 group-hover:scale-110 transition-transform">
                    {link.icon}
                  </span>
                  <span className="text-xs md:text-sm font-medium text-stone-700 group-hover:text-amber-800">
                    {link.label}
                  </span>
                </a>
              ))}
            </div>
          </section> */}

          </div>
        </div>

        {/* Footer - 新版設計（在白色卡片外面） */}
        <footer className="relative z-10 pt-12 pb-12 mt-0">
          <div className="px-6 text-center">
            {/* Logo 區域 */}
            <div className="flex justify-center mb-6">
              <img src="/image/logo.png" alt="中台山" className="h-14 w-auto" />
            </div>

            {/* 紅色裝飾線 */}
            <div className="w-12 h-1 mx-auto mb-6" style={{ backgroundColor: 'var(--color-accent-red)' }}></div>

            {/* 標題 */}
            <h4 className="text-lg tracking-[0.4em] mb-6" style={{ color: 'var(--color-accent)' }}>
              {currentLanguage.code === 'zh-TW' ? t('login.heroTitle').split('').join(' ') : t('login.heroTitle')}
            </h4>

            <div className="mb-6">
              <p className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-sub)' }}>CONTACT & INFO</p>
            </div>
            {/* 聯絡資訊 - 置中排列 */}
            <div className="text-sm font-light leading-6 space-y-1 max-w-md mx-auto mb-6" style={{ color: 'var(--color-text-sub)' }}>
              <p>Copyright © 2026 Chung Tai Chan Monastery</p>
              <p>{t('footer.address')}</p>
              <p>{t('footer.phoneFax')}</p>
              <p>{t('footer.accountName')}</p>
              <p>Email: ctworld@mail.ctcm.org.tw</p>
              <p>Version: {version}</p>
            </div>

            {/* 底部英文 - 金色 */}
            <div className="text-sm font-light tracking-[0.2em] mb-8" style={{ color: 'var(--color-accent)' }}>
              CHUNG TAI CHAN MONASTERY<br />
              <span className="uppercase mt-1 block">35TH ANNIVERSARY</span>
            </div>

            {/* 手機版 BottomNav 佔位 */}
            <div className="md:hidden" style={{ height: 'calc(80px + env(safe-area-inset-bottom))' }} />
          </div>
        </footer>
      </div>

      {/* 消息 Popup Modal */}
      {isNewsModalOpen && selectedNews && (
        <div
          className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm animate-fadeIn"
          onClick={handleCloseNewsModal}
        >
          <div
            className="relative w-full max-w-[430px] animate-slideUp"
            style={{ background: '#FDF8F2', borderRadius: '20px 20px 0 0', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 8 }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(110,98,91,0.3)' }} />
            </div>
            <CloseButton btnColor="#6E625B" onClick={handleCloseNewsModal} />
            <div style={{ overflowY: 'auto', padding: '0 24px 32px' }}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', paddingBottom: 8 }}>
                <span style={{ color: '#BC3823', fontSize: 20, fontFamily: 'Noto Serif TC', fontWeight: 500 }}>{selectedNews.date.month} /</span>
                <span style={{ color: '#BC3823', fontSize: 20, fontFamily: 'Noto Serif TC', fontWeight: 700 }}>{selectedNews.date.day}</span>
              </div>
              <h3 style={{ color: '#171717', fontSize: 22, fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '1.5', marginBottom: 16 }}>{selectedNews.title}</h3>
              {selectedNews.description && (
                <>
                  <div style={{ height: 1, background: 'rgba(110,98,91,0.2)', marginBottom: 16 }} />
                  <p style={{ color: 'rgba(23,23,23,0.80)', fontSize: 17, fontFamily: 'Noto Serif TC', fontWeight: 400, lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>{selectedNews.description}</p>
                </>
              )}
              {selectedNews.postId && (
                <button
                  type="button"
                  onClick={() => handleNewsNavigate(selectedNews)}
                  className="flex items-center justify-center gap-2 mt-6 w-full"
                  style={{ padding: '12px 0', borderRadius: 8, border: '1px solid #BC313D', color: '#BC313D', fontSize: 16, fontFamily: 'Noto Serif TC', fontWeight: 500, background: 'transparent', cursor: 'pointer' }}
                >
                  {getNewsNavigateLabel(selectedNews)}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 明信片製作 Modal */}
      <PostcardCreateModal isOpen={isPostcardModalOpen} onClose={() => setIsPostcardModalOpen(false)} />

      {/* 影片播放器 Modal */}
      {isVideoModalOpen && selectedVideo && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fadeIn p-4 md:p-6 overflow-y-auto"
          onClick={handleCloseVideoModal}
        >
          {/* 關閉按鈕 - 固定在畫面右上角 */}
          <CloseButton btnColor="bg-white/10" onClick={handleCloseVideoModal} />
          <div
            className="relative w-full max-w-4xl my-auto animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 影片播放器容器 */}
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              {getYouTubeVideoId(selectedVideo.videoUrl) ? (
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(selectedVideo.videoUrl)}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1`}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="eager"
                  className="absolute inset-0 w-full h-full"
                />
              ) : (
                <video
                  src={selectedVideo.videoUrl}
                  title={selectedVideo.title}
                  controls
                  autoPlay
                  playsInline
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  className="absolute inset-0 w-full h-full"
                />
              )}
            </div>

            {/* 影片資訊 */}
            <div className="mt-4 md:mt-6 px-2">
              <h3 className="text-white text-lg md:text-xl lg:text-2xl font-bold mb-2">{selectedVideo.title}</h3>
              {selectedVideo.description && (
                <p className="text-white/70 text-sm md:text-base">{selectedVideo.description}</p>
              )}
              <div className="flex items-center gap-4 mt-3 text-white/60 text-xs md:text-sm">
                {selectedVideo.duration && (
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-base">schedule</span>
                    {selectedVideo.duration}
                  </span>
                )}
                {selectedVideo.views && (
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-base">visibility</span>
                    {t('home.videoViews').replace('{{count}}', selectedVideo.views)}
                  </span>
                )}
                {selectedVideo.uploadDate && (
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-base">calendar_today</span>
                    {selectedVideo.uploadDate}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default LandingPage;
